/**
 * sharedStyles.ts
 *
 * Inline-style objects shared across multiple step components and the panel.
 * V2 uses inline styles (no new CSS file) so the page remains self-contained
 * and doesn't pollute the global stylesheet.
 *
 * Step-specific styles (e.g. template grid, angle buttons) live in the
 * component file that uses them to keep coupling minimal.
 */

import React from "react";

// ── Brand palette ─────────────────────────────────────────────────────────────

/** Primary accent colour used throughout the V2 UI. */
export const ACCENT = "#5B4FE8";

/** Pre-built rgba() variants of ACCENT at common opacities. */
export const ACCENT_08  = "rgba(91,79,232,0.08)";
export const ACCENT_12  = "rgba(91,79,232,0.12)";
export const ACCENT_18  = "rgba(91,79,232,0.18)";
export const ACCENT_20  = "rgba(91,79,232,0.20)";
export const ACCENT_40  = "rgba(91,79,232,0.40)";

/** Shared CSS animation for small loading spinners (used in multiple steps). */
export const spinnerStyle: React.CSSProperties = {
  width:        "20px",
  height:       "20px",
  borderRadius: "50%",
  border:       `2.5px solid ${ACCENT_18}`,
  borderTopColor: ACCENT,
  animation:    "spin 0.9s linear infinite",
  flexShrink:   0,
};

// ── Layout ────────────────────────────────────────────────────────────────────

export const pageStyle: React.CSSProperties = {
  fontFamily: "'Bricolage Grotesque', sans-serif",
  background: "#FAFAF9",
  minHeight:  "100vh",
  color:      "#0A0A0A",
};

export const navStyle: React.CSSProperties = {
  display:         "flex",
  alignItems:      "center",
  justifyContent:  "space-between",
  padding:         "0 32px",
  height:          "56px",
  background:      "rgba(250,250,249,0.94)",
  backdropFilter:  "blur(16px)",
  borderBottom:    "1px solid #E4E4E0",
  position:        "sticky",
  top:             0,
  zIndex:          999,
};

export const mainStyle: React.CSSProperties = {
  maxWidth:       "660px",
  margin:         "0 auto",
  padding:        "48px 24px 80px",
  display:        "flex",
  flexDirection:  "column",
  gap:            "28px",
};

// ── Typography ────────────────────────────────────────────────────────────────

export const stepHeading: React.CSSProperties = {
  fontSize:      "22px",
  fontWeight:    800,
  letterSpacing: "-0.03em",
  marginBottom:  "4px",
};

export const stepSub: React.CSSProperties = {
  fontSize:     "14px",
  color:        "#888",
  marginBottom: "20px",
};

// ── Card ──────────────────────────────────────────────────────────────────────

export const card: React.CSSProperties = {
  background:   "#fff",
  border:       "1.5px solid #E4E4E0",
  borderRadius: "16px",
  padding:      "20px",
  boxShadow:    "0 2px 12px rgba(0,0,0,0.05)",
};

// ── Inputs ────────────────────────────────────────────────────────────────────

export const textarea: React.CSSProperties = {
  width:       "100%",
  border:      "none",
  outline:     "none",
  resize:      "none",
  fontFamily:  "'Bricolage Grotesque', sans-serif",
  fontSize:    "15px",
  lineHeight:  "1.6",
  color:       "#0A0A0A",
  background:  "transparent",
};

// ── Buttons ───────────────────────────────────────────────────────────────────

export const primaryBtn: React.CSSProperties = {
  background:     ACCENT,
  color:          "#fff",
  border:         "none",
  borderRadius:   "12px",
  padding:        "12px 24px",
  fontSize:       "14px",
  fontWeight:     700,
  cursor:         "pointer",
  letterSpacing:  "-0.01em",
  transition:     "opacity 0.15s, transform 0.15s",
  display:        "inline-flex",
  alignItems:     "center",
  gap:            "6px",
};

export const secondaryBtn: React.CSSProperties = {
  background:    "transparent",
  color:         ACCENT,
  border:        `1.5px solid ${ACCENT}`,
  borderRadius:  "12px",
  padding:       "12px 24px",
  fontSize:      "14px",
  fontWeight:    700,
  cursor:        "pointer",
  letterSpacing: "-0.01em",
  transition:    "background 0.15s",
};

export const ghostBtn: React.CSSProperties = {
  background:  "none",
  border:      "none",
  color:       "#888",
  padding:     "12px 0",
  fontSize:    "13px",
  cursor:      "pointer",
  fontFamily:  "inherit",
  fontWeight:  600,
};
